/**
 * ══════════════════════════════════════════════════════════════
 * NETUNO — Engine de Recomendações Operacionais
 * ══════════════════════════════════════════════════════════════
 *
 * Gera recomendações de mitigação baseadas em insights e
 * diagnósticos analíticos. Cada gerador é vinculado a um
 * ruleCode do InsightEngine, mantendo rastreabilidade completa.
 *
 * Uso: import { generateRecommendations } from '@/lib/recommendationEngine';
 * ══════════════════════════════════════════════════════════════
 */

import type { AnalyticalInsight } from '@/types/insights';
import type { RootCauseAnalysis, DiagnosticContext } from '@/types/diagnostics';
import type {
  OperationalRecommendation,
  RecommendationPriority,
  EffortLevel,
  RecommendationTarget,
  RecommendationCategory,
  ImpactEstimate,
} from '@/types/recommendations';
import { PRIORITY_ORDER } from '@/types/recommendations';

// ─── Generator type ─────────────────────────────────────────

type RecommendationGenerator = (
  insight: AnalyticalInsight,
  diagnostic: RootCauseAnalysis,
  ctx: DiagnosticContext,
) => OperationalRecommendation[];

// ─── Helper ─────────────────────────────────────────────────

let _seqId = 0;
function makeId(prefix: string): string {
  return `rec-${prefix}-${++_seqId}-${Date.now().toString(36)}`;
}

function buildRec(
  insight: AnalyticalInsight,
  diagnostic: RootCauseAnalysis,
  overrides: {
    title: string;
    description: string;
    priority: RecommendationPriority;
    estimatedImpact: ImpactEstimate;
    effortLevel: EffortLevel;
    targetEntities: RecommendationTarget[];
    suggestedOwner: string;
    category: RecommendationCategory;
    drillDownRoute?: string;
  },
): OperationalRecommendation {
  return {
    id: makeId(overrides.category),
    relatedInsightId: insight.id,
    relatedDiagnosticId: diagnostic.id,
    generatedAt: new Date().toISOString(),
    ...overrides,
  };
}

// ─── Generators ─────────────────────────────────────────────

const GENERATORS: Record<string, RecommendationGenerator> = {

  // ── R1: Concentração de risco
  RISK_CONCENTRATION: (insight, diag, ctx) => {
    const recs: OperationalRecommendation[] = [];
    const criticos = ctx.contracts?.filter(c => c.nivel_criticidade === 'critico') ?? [];

    // Cluster-based recommendations
    const clusters = new Map<string, typeof criticos>();
    criticos.forEach(c => {
      const k = c.cluster_nome || 'Sem cluster';
      clusters.set(k, [...(clusters.get(k) || []), c]);
    });
    const topCluster = [...clusters.entries()].sort((a, b) => b[1].length - a[1].length)[0];

    if (topCluster && topCluster[1].length >= 2) {
      recs.push(buildRec(insight, diag, {
        title: `Redistribuir supervisão no cluster "${topCluster[0]}"`,
        description: `O cluster concentra ${topCluster[1].length} contratos críticos. Recomenda-se designar supervisor dedicado e intensificar inspeções locais para mitigar o risco regional.`,
        priority: 'high',
        effortLevel: 'medium',
        category: 'resource_reallocation',
        suggestedOwner: 'Coordenador Regional',
        drillDownRoute: '/empreendimentos/inteligencia/uo',
        estimatedImpact: {
          label: 'Redução da concentração regional',
          scoreReduction: 10,
          mitigationPct: 20,
          description: 'Supervisão local dedicada reduz tempo de resposta a desvios em ~20%.',
        },
        targetEntities: topCluster[1].slice(0, 3).map(c => ({
          type: 'contract' as const,
          id: c.contract_id,
          name: c.contrato_nome,
          drillDownRoute: `/empreendimentos/inteligencia/${c.contract_id}`,
        })),
      }));
    }

    recs.push(buildRec(insight, diag, {
      title: 'Convocar comitê de risco para revisão de carteira',
      description: `${criticos.length} contratos em nível crítico demandam revisão executiva com planos de ação individualizados.`,
      priority: 'critical',
      effortLevel: 'low',
      category: 'executive_escalation',
      suggestedOwner: 'Diretor de Operações',
      estimatedImpact: {
        label: 'Visibilidade executiva',
        mitigationPct: 15,
        description: 'Decisões estratégicas top-down aceleram resolução de riscos sistêmicos.',
      },
      targetEntities: [{
        type: 'portfolio',
        id: 'portfolio',
        name: 'Carteira de Contratos',
        drillDownRoute: '/empreendimentos/inteligencia',
      }],
    }));

    return recs;
  },

  // ── R2: Deterioração da carteira
  PORTFOLIO_DETERIORATION: (insight, diag, ctx) => {
    const piorando = ctx.contracts?.filter(c => c.tendencia === 'piorando') ?? [];
    const recs: OperationalRecommendation[] = [];

    recs.push(buildRec(insight, diag, {
      title: 'Intensificar monitoramento de contratos em deterioração',
      description: `${piorando.length} contratos apresentam tendência de piora. Reduzir intervalo entre inspeções e implementar checkpoints semanais de progresso.`,
      priority: 'high',
      effortLevel: 'medium',
      category: 'monitoring_increase',
      suggestedOwner: 'Gerente de Fiscalização',
      drillDownRoute: '/empreendimentos/inteligencia/tendencias',
      estimatedImpact: {
        label: 'Detecção precoce de desvios',
        mitigationPct: 25,
        description: 'Monitoramento intensivo permite intervenção antes que desvios se tornem irreversíveis.',
      },
      targetEntities: piorando.slice(0, 4).map(c => ({
        type: 'contract' as const,
        id: c.contract_id,
        name: c.contrato_nome,
        drillDownRoute: `/empreendimentos/inteligencia/${c.contract_id}`,
      })),
    }));

    const critPiorando = piorando.filter(c => c.nivel_criticidade === 'critico');
    if (critPiorando.length >= 1) {
      recs.push(buildRec(insight, diag, {
        title: 'Avaliar renegociação de prazos em contratos críticos',
        description: `${critPiorando.length} contrato(s) crítico(s) em deterioração contínua. Considerar repactuação de cronograma para viabilizar recuperação.`,
        priority: 'critical',
        effortLevel: 'high',
        category: 'contract_renegotiation',
        suggestedOwner: 'Gerente de Contrato',
        estimatedImpact: {
          label: 'Viabilizar recuperação de prazo',
          scoreReduction: 15,
          description: 'Repactuação realista permite replanejar ações corretivas com metas atingíveis.',
        },
        targetEntities: critPiorando.slice(0, 3).map(c => ({
          type: 'contract' as const,
          id: c.contract_id,
          name: c.contrato_nome,
          drillDownRoute: `/empreendimentos/inteligencia/${c.contract_id}`,
        })),
      }));
    }

    return recs;
  },

  // ── R3: Contrato com score extremo
  HIGH_SCORE_CONTRACT: (insight, diag, ctx) => {
    const contract = ctx.contracts?.find(c => c.contract_id === insight.relatedEntity?.id);
    if (!contract) return [];

    const recs: OperationalRecommendation[] = [];

    recs.push(buildRec(insight, diag, {
      title: `Plano de recuperação para "${contract.contrato_nome}"`,
      description: `Score de criticidade em ${contract.score_criticidade}/100. Elaborar plano de recuperação com marcos semanais, alocação de equipe de apoio e revisão de aquisições.`,
      priority: 'critical',
      effortLevel: 'high',
      category: 'schedule_intervention',
      suggestedOwner: 'Gerente de Contrato',
      drillDownRoute: `/empreendimentos/inteligencia/${contract.contract_id}`,
      estimatedImpact: {
        label: 'Recuperação de cronograma',
        scoreReduction: 20,
        mitigationPct: 30,
        description: 'Plano de recuperação estruturado reduz risco de atraso em ~30%.',
      },
      targetEntities: [{
        type: 'contract',
        id: contract.contract_id,
        name: contract.contrato_nome,
        drillDownRoute: `/empreendimentos/inteligencia/${contract.contract_id}`,
      }],
    }));

    if ((contract.prob_atraso_mc ?? 0) > 70) {
      recs.push(buildRec(insight, diag, {
        title: `Acionar contingência de prazo`,
        description: `Probabilidade de atraso em ${contract.prob_atraso_mc}%. Ativar equipe adicional e/ou revisar sequenciamento de atividades críticas.`,
        priority: 'high',
        effortLevel: 'medium',
        category: 'resource_reallocation',
        suggestedOwner: 'Coordenador de Obra',
        estimatedImpact: {
          label: 'Aceleração da execução',
          mitigationPct: 20,
          description: 'Reforço de equipe em atividades do caminho crítico reduz prazo previsto.',
        },
        targetEntities: [{
          type: 'contract',
          id: contract.contract_id,
          name: contract.contrato_nome,
          drillDownRoute: `/empreendimentos/inteligencia/${contract.contract_id}`,
        }],
      }));
    }

    return recs;
  },

  // ── R4: Alto volume de ocorrências
  HIGH_OCCURRENCES: (insight, diag, ctx) => {
    const contract = ctx.contracts?.find(c => c.contract_id === insight.relatedEntity?.id);
    if (!contract) return [];

    return [
      buildRec(insight, diag, {
        title: `Auditoria de qualidade em "${contract.contrato_nome}"`,
        description: `${contract.total_ocorrencias} ocorrências registradas. Realizar auditoria técnica in loco e implementar checklist obrigatório antes de liberação de frentes.`,
        priority: 'high',
        effortLevel: 'medium',
        category: 'quality_review',
        suggestedOwner: 'Engenheiro de Qualidade',
        drillDownRoute: `/empreendimentos/inteligencia/${contract.contract_id}`,
        estimatedImpact: {
          label: 'Redução de não-conformidades',
          mitigationPct: 35,
          description: 'Auditoria com checklist reduz reincidência de não-conformidades em ~35%.',
        },
        targetEntities: [{
          type: 'contract',
          id: contract.contract_id,
          name: contract.contrato_nome,
          drillDownRoute: `/empreendimentos/inteligencia/${contract.contract_id}`,
        }],
      }),
      buildRec(insight, diag, {
        title: 'Implementar treinamento preventivo',
        description: 'Capacitar equipe de campo sobre principais não-conformidades detectadas para reduzir recorrência.',
        priority: 'medium',
        effortLevel: 'low',
        category: 'preventive_action',
        suggestedOwner: 'Supervisor de Campo',
        estimatedImpact: {
          label: 'Prevenção de reincidência',
          mitigationPct: 15,
          description: 'Treinamentos focados nas causas mais frequentes reduzem ocorrências futuras.',
        },
        targetEntities: [{
          type: 'contract',
          id: contract.contract_id,
          name: contract.contrato_nome,
        }],
      }),
    ];
  },

  // ── R5: Concentração por UO
  UO_RISK_CONCENTRATION: (insight, diag, ctx) => {
    const criticalUOs = ctx.uos?.filter(u => u.nivel_risco === 'critico') ?? [];

    return [
      buildRec(insight, diag, {
        title: 'Redirecionar recursos para UOs críticas',
        description: `${criticalUOs.length} UO(s) em nível crítico. Priorizar alocação de fiscais e supervisores nessas regiões.`,
        priority: 'high',
        effortLevel: 'medium',
        category: 'resource_reallocation',
        suggestedOwner: 'Coordenador Regional',
        drillDownRoute: '/empreendimentos/inteligencia/uo',
        estimatedImpact: {
          label: 'Melhoria da cobertura regional',
          mitigationPct: 20,
          description: 'Presença operacional intensificada reduz tempo de resposta a desvios.',
        },
        targetEntities: criticalUOs.slice(0, 4).map(u => ({
          type: 'uo' as const,
          id: u.uo_id,
          name: u.uo_nome,
        })),
      }),
    ];
  },

  // ── R6: Melhoria da carteira (recomendações de manutenção)
  PORTFOLIO_IMPROVEMENT: (insight, diag, _ctx) => {
    return [
      buildRec(insight, diag, {
        title: 'Documentar práticas que geraram melhoria',
        description: 'Registrar as ações que levaram à melhoria dos indicadores para replicar em contratos ainda críticos.',
        priority: 'low',
        effortLevel: 'low',
        category: 'preventive_action',
        suggestedOwner: 'Analista de Performance',
        estimatedImpact: {
          label: 'Replicação de boas práticas',
          mitigationPct: 10,
          description: 'Disseminar lições aprendidas previne regressão e acelera melhoria em outros contratos.',
        },
        targetEntities: [{
          type: 'portfolio',
          id: 'portfolio',
          name: 'Carteira de Contratos',
        }],
      }),
    ];
  },

  // ── R7: Risco crescente
  GROWING_RISK_TREND: (insight, diag, ctx) => {
    const contract = ctx.contracts?.find(c => c.contract_id === insight.relatedEntity?.id);
    if (!contract) return [];

    return [
      buildRec(insight, diag, {
        title: `Intervenção preventiva em "${contract.contrato_nome}"`,
        description: `Tendência crescente detectada. Antecipar reunião com contratada para identificar causas e ajustar cronograma antes que o risco se materialize.`,
        priority: 'high',
        effortLevel: 'low',
        category: 'schedule_intervention',
        suggestedOwner: 'Gerente de Contrato',
        drillDownRoute: `/empreendimentos/inteligencia/${contract.contract_id}`,
        estimatedImpact: {
          label: 'Prevenção de escalação',
          scoreReduction: 10,
          mitigationPct: 25,
          description: 'Intervenção precoce pode reverter tendência antes que atinja nível crítico.',
        },
        targetEntities: [{
          type: 'contract',
          id: contract.contract_id,
          name: contract.contrato_nome,
          drillDownRoute: `/empreendimentos/inteligencia/${contract.contract_id}`,
        }],
      }),
      buildRec(insight, diag, {
        title: 'Solicitar relatório atualizado da contratada',
        description: 'Exigir relatório de progresso detalhado com explicação dos desvios e plano de recuperação.',
        priority: 'medium',
        effortLevel: 'low',
        category: 'monitoring_increase',
        suggestedOwner: 'Fiscal de Contrato',
        estimatedImpact: {
          label: 'Transparência e accountability',
          mitigationPct: 10,
          description: 'Relatórios detalhados identificam causas reais de atraso e permitem ação corretiva direcionada.',
        },
        targetEntities: [{
          type: 'contract',
          id: contract.contract_id,
          name: contract.contrato_nome,
        }],
      }),
    ];
  },
};

// ─── Public API ─────────────────────────────────────────────

/**
 * Generates operational recommendations from an insight + diagnostic pair.
 * Returns recommendations sorted by priority.
 */
export function generateRecommendations(
  insight: AnalyticalInsight,
  diagnostic: RootCauseAnalysis,
  ctx: DiagnosticContext,
): OperationalRecommendation[] {
  const ruleCode = insight.ruleCode;
  const generator = GENERATORS[ruleCode];

  if (!generator) {
    // Fallback: generic recommendation
    return [buildRec(insight, diagnostic, {
      title: 'Investigar causa raiz do risco detectado',
      description: `Insight "${insight.title}" requer investigação detalhada. Designar responsável e prazo para análise.`,
      priority: 'medium',
      effortLevel: 'low',
      category: 'monitoring_increase',
      suggestedOwner: 'Analista de Risco',
      estimatedImpact: {
        label: 'Diagnóstico aprofundado',
        description: 'Investigação estruturada identifica causas e viabiliza ações corretivas eficazes.',
      },
      targetEntities: [],
    })];
  }

  const recs = generator(insight, diagnostic, ctx);

  // Sort by priority
  return recs.sort((a, b) => PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority]);
}
